/**
 * ZAPHUB 2.1 — LoginPage
 * Login + Registro + Demo
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/apiClient';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Zap, Loader2, Play, UserPlus, LogIn } from 'lucide-react';
import { toast } from 'sonner';

type Mode = 'login' | 'register';

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [department, setDepartment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login({ email, password });
      toast.success('¡Bienvenido a ZAPHUB!');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error?.error?.message || error?.message || 'Credenciales incorrectas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/auth/register', { email, password, name, department: department || undefined });
      toast.success('¡Cuenta creada! Iniciando sesión...');
      // Auto-login after register
      await login({ email, password });
      navigate('/dashboard');
    } catch (error: any) {
      const msg = error?.error?.message || error?.message || 'Error al crear la cuenta';
      toast.error(msg === 'Email already registered' ? 'Este email ya está registrado' : msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemo = async () => {
    setIsDemoLoading(true);
    try {
      const res = await api.post('/auth/demo');
      const { user, auth } = (res as any).data;
      localStorage.setItem('zaphub_token', auth.accessToken);
      if (auth.refreshToken) localStorage.setItem('zaphub_refresh_token', auth.refreshToken);
      localStorage.setItem('zaphub_user', JSON.stringify(user));
      toast.success('Sesión demo iniciada — ¡explora libremente!');
      window.location.href = '/dashboard';
    } catch {
      toast.error('El modo demo no está disponible en este momento');
    } finally {
      setIsDemoLoading(false);
    }
  };

  const busy = isLoading || isDemoLoading;

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      {/* Grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-5">
            <img src="/logo-zaphub.png" alt="ZAPHUB" className="w-16 h-16 object-contain" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mb-1">ZAPHUB</h1>
          <p className="text-sm text-slate-500">Enterprise Automation Platform</p>
        </div>

        {/* Card */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 blur-3xl" />
          <div className="relative bg-white rounded-2xl shadow-xl border border-slate-200 p-8">

            {/* Mode tabs */}
            <div className="flex rounded-xl bg-slate-100 p-1 mb-6">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === 'login' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <LogIn className="w-4 h-4" /> Iniciar sesión
              </button>
              <button
                onClick={() => setMode('register')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === 'register' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <UserPlus className="w-4 h-4" /> Registrarse
              </button>
            </div>

            {/* Login form */}
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nombre@empresa.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    disabled={busy}
                    className="h-11 bg-slate-50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    disabled={busy}
                    className="h-11 bg-slate-50"
                  />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 shadow-lg shadow-primary/25">
                  {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Entrando...</> : 'Iniciar sesión'}
                </Button>
              </form>
            )}

            {/* Register form */}
            {mode === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Nombre completo</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Tu nombre"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    disabled={busy}
                    className="h-11 bg-slate-50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-email">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="nombre@empresa.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    disabled={busy}
                    className="h-11 bg-slate-50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-password">Contraseña <span className="text-slate-400 text-xs">(mín. 8 caracteres)</span></Label>
                  <Input
                    id="reg-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    disabled={busy}
                    className="h-11 bg-slate-50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="department">Departamento <span className="text-slate-400 text-xs">(opcional)</span></Label>
                  <Input
                    id="department"
                    type="text"
                    placeholder="Ventas, IT, RRHH..."
                    value={department}
                    onChange={e => setDepartment(e.target.value)}
                    disabled={busy}
                    className="h-11 bg-slate-50"
                  />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 shadow-lg shadow-primary/25">
                  {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando cuenta...</> : <><UserPlus className="w-4 h-4 mr-2" /> Crear cuenta gratis</>}
                </Button>
                <p className="text-xs text-center text-slate-400">
                  Al registrarte aceptas nuestros términos de uso. Plan Starter gratuito incluido.
                </p>
              </form>
            )}

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400">o prueba sin cuenta</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Demo button */}
            <Button
              type="button"
              variant="outline"
              onClick={handleDemo}
              disabled={busy}
              className="w-full h-11 border-violet-300 text-violet-700 hover:bg-violet-50"
            >
              {isDemoLoading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cargando demo...</>
                : <><Play className="w-4 h-4 mr-2" /> Probar Demo — Sin registro</>
              }
            </Button>
            <p className="text-xs text-center text-slate-400 mt-2">
              Sesión demo de 1 hora · Los datos se reinician cada 24h
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          © 2025 ZAPHUB — <a href="https://ffollowme.com" className="hover:text-primary">FFOLLOWME OÜ</a>
        </p>
      </div>
    </div>
  );
}
